// ============================================================================
// PACKET SERIALIZER - Binary Encoding/Decoding
// ============================================================================

import { Packet, PacketType, PlayerInput, PlayerState, ProjectileState, ObjectiveState } from '@/shared/types';

// Simple binary format for performance
// Format: [type:u8][timestamp:f64][senderId length:u8][senderId:string][payload...]

export class PacketSerializer {
  private encoder = new TextEncoder();
  private decoder = new TextDecoder();

  serialize(packet: Packet): ArrayBuffer {
    // For now, use JSON serialization for simplicity
    // Can optimize to binary later for performance
    const json = JSON.stringify(packet);
    return this.encoder.encode(json).buffer;
  }

  deserialize(buffer: ArrayBuffer): Packet {
    const text = this.decoder.decode(buffer);
    return JSON.parse(text);
  }

  // Optimized binary serialization for input packets
  serializeInput(input: PlayerInput, senderId: string): ArrayBuffer {
    const buffer = new ArrayBuffer(64);
    const view = new DataView(buffer);
    let offset = 0;

    // Type
    view.setUint8(offset, PacketType.Input);
    offset += 1;

    // Timestamp
    view.setFloat64(offset, Date.now());
    offset += 8;

    // Sender ID length and string
    const senderBytes = this.encoder.encode(senderId);
    view.setUint8(offset, senderBytes.length);
    offset += 1;
    new Uint8Array(buffer, offset, senderBytes.length).set(senderBytes);
    offset += senderBytes.length;

    // Input data
    view.setFloat32(offset, input.moveX);
    offset += 4;
    view.setFloat32(offset, input.moveY);
    offset += 4;
    view.setFloat32(offset, input.aimX);
    offset += 4;
    view.setFloat32(offset, input.aimY);
    offset += 4;

    // Bit flags for booleans
    let flags = 0;
    if (input.shoot) flags |= 1;
    if (input.reload) flags |= 2;
    if (input.interact) flags |= 4;
    if (input.ability1) flags |= 8;
    if (input.ability2) flags |= 16;
    if (input.ultimate) flags |= 32;
    view.setUint8(offset, flags);
    offset += 1;

    // Sequence
    view.setUint32(offset, input.sequence);
    offset += 4;

    return buffer.slice(0, offset);
  }

  deserializeInput(buffer: ArrayBuffer): { senderId: string; input: PlayerInput; timestamp: number } | null {
    try {
      const view = new DataView(buffer);
      let offset = 0;

      // Type
      const type = view.getUint8(offset);
      if (type !== PacketType.Input) return null;
      offset += 1;

      // Timestamp
      const timestamp = view.getFloat64(offset);
      offset += 8;

      // Sender ID
      const senderLength = view.getUint8(offset);
      offset += 1;
      const senderId = this.decoder.decode(new Uint8Array(buffer, offset, senderLength));
      offset += senderLength;

      // Input data
      const moveX = view.getFloat32(offset);
      offset += 4;
      const moveY = view.getFloat32(offset);
      offset += 4;
      const aimX = view.getFloat32(offset);
      offset += 4;
      const aimY = view.getFloat32(offset);
      offset += 4;

      // Flags
      const flags = view.getUint8(offset);
      offset += 1;

      // Sequence
      const sequence = view.getUint32(offset);

      const input: PlayerInput = {
        moveX,
        moveY,
        aimX,
        aimY,
        shoot: (flags & 1) !== 0,
        reload: (flags & 2) !== 0,
        interact: (flags & 4) !== 0,
        throwGrenade: (flags & 64) !== 0,
        useBombCall: false,
        useMissileStrike: false,
        ability1: (flags & 8) !== 0,
        ability2: (flags & 16) !== 0,
        ultimate: (flags & 32) !== 0,
        weaponSlot: 0,  // TODO: serialize weapon slot when networking is implemented
        sequence,
      };

      return { senderId, input, timestamp };
    } catch {
      return null;
    }
  }

  // Snapshot serialization (simplified for now)
  serializeSnapshot(
    tick: number,
    players: PlayerState[],
    projectiles: ProjectileState[],
    objectives: ObjectiveState
  ): ArrayBuffer {
    const packet = {
      type: PacketType.StateSnapshot,
      timestamp: Date.now(),
      senderId: 'host',
      data: { tick, players, projectiles, objectives },
    };
    return this.serialize(packet);
  }
}
